import { Shield, Globe, Link2, RefreshCw, Check, Ban } from "lucide-react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

export default function FraudDetailsDialog({
  open,
  onOpenChange,
  selectedUserFraud,
  getRiskLevelBadge,
  onRecheckFraud,
  onClearFraudFlag,
  onSuspendUser,
}) {
  if (!selectedUserFraud) return null;

  const fraudCheck = selectedUserFraud.signup_metadata?.fraud_check;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Fraud Detection Details
          </DialogTitle>
          <DialogDescription>
            Signup fraud analysis for {selectedUserFraud?.email || "this user"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-4" data-testid="fraud-details-content">
          {/* User Info */}
          <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium text-slate-900">User Information</h4>
              <Badge variant="outline" className="capitalize">
                {selectedUserFraud.status}
              </Badge>
            </div>
            <p className="text-sm text-slate-600">{selectedUserFraud.email}</p>
            <p className="text-xs text-slate-500 mt-1">
              Registered:{" "}
              {selectedUserFraud.created_at
                ? new Date(selectedUserFraud.created_at).toLocaleString()
                : "N/A"}
            </p>
          </div>

          {fraudCheck ? (
            <>
              {/* Risk Overview */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div
                  className={`rounded-lg p-4 ${getRiskLevelBadge(
                    fraudCheck.risk_level
                  )} border`}
                >
                  <p className="text-xs opacity-75 mb-1">Risk Level</p>
                  <p className="text-xl font-bold capitalize">
                    {fraudCheck.risk_level || "Unknown"}
                  </p>
                </div>
                <div className="bg-slate-100 rounded-lg p-4 border border-slate-200">
                  <p className="text-xs text-slate-500 mb-1">Fraud Score</p>
                  <p
                    className={`text-xl font-bold ${
                      fraudCheck.fraud_score >= 75
                        ? "text-red-600"
                        : fraudCheck.fraud_score >= 50
                        ? "text-orange-600"
                        : fraudCheck.fraud_score >= 25
                        ? "text-amber-600"
                        : "text-green-600"
                    }`}
                  >
                    {fraudCheck.fraud_score || 0}/100
                  </p>
                </div>
                <div className="bg-slate-100 rounded-lg p-4 border border-slate-200">
                  <p className="text-xs text-slate-500 mb-1">Flagged</p>
                  <p
                    className={`text-xl font-bold ${
                      fraudCheck.should_flag ? "text-red-600" : "text-green-600"
                    }`}
                  >
                    {fraudCheck.should_flag ? "Yes" : "No"}
                  </p>
                </div>
                <div className="bg-slate-100 rounded-lg p-4 border border-slate-200">
                  <p className="text-xs text-slate-500 mb-1">Checked At</p>
                  <p className="text-sm font-medium text-slate-700">
                    {fraudCheck.checked_at
                      ? new Date(fraudCheck.checked_at).toLocaleDateString()
                      : "N/A"}
                  </p>
                </div>
              </div>

              {/* Risk Indicators */}
              <div className="bg-white rounded-lg p-4 border border-slate-200">
                <h4 className="font-medium text-slate-900 mb-3">
                  Risk Indicators
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {[
                    { label: "VPN", value: fraudCheck.is_vpn, type: "warning" },
                    { label: "Proxy", value: fraudCheck.is_proxy, type: "warning" },
                    { label: "Tor", value: fraudCheck.is_tor, type: "danger" },
                    { label: "Bot", value: fraudCheck.is_bot, type: "danger" },
                    { label: "Recent Abuse", value: fraudCheck.recent_abuse, type: "danger" },
                  ].map(({ label, value, type }) => (
                    <div
                      key={label}
                      className="flex items-center justify-between p-2 bg-slate-50 rounded"
                    >
                      <span className="text-sm text-slate-600">{label}</span>
                      <Badge
                        className={
                          value
                            ? type === "danger"
                              ? "bg-red-100 text-red-700"
                              : "bg-amber-100 text-amber-700"
                            : "bg-green-100 text-green-700"
                        }
                      >
                        {value ? "Yes" : "No"}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>

              {/* Location & Network Info */}
              {(fraudCheck.location || fraudCheck.network) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {fraudCheck.location && (
                    <div className="bg-white rounded-lg p-4 border border-slate-200">
                      <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                        <Globe className="w-4 h-4" />
                        Location
                      </h4>
                      <div className="space-y-2 text-sm">
                        {["country", "city", "region", "timezone"].map((field) => (
                          <div key={field} className="flex justify-between">
                            <span className="text-slate-500 capitalize">{field}</span>
                            <span className="font-medium">
                              {fraudCheck.location[field] || "N/A"}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {fraudCheck.network && (
                    <div className="bg-white rounded-lg p-4 border border-slate-200">
                      <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                        <Link2 className="w-4 h-4" />
                        Network
                      </h4>
                      <div className="space-y-2 text-sm">
                        {[
                          { key: "isp", label: "ISP" },
                          { key: "organization", label: "Organization" },
                          { key: "host", label: "Host" },
                        ].map(({ key, label }) => (
                          <div key={key} className="flex justify-between">
                            <span className="text-slate-500">{label}</span>
                            <span className="font-medium truncate max-w-[150px]">
                              {fraudCheck.network[key] || "N/A"}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Signup Metadata */}
              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                <h4 className="font-medium text-slate-900 mb-3">Signup Details</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">IP Address</span>
                    <span className="font-mono text-slate-700">
                      {selectedUserFraud.signup_metadata.ip_address || "N/A"}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500">User Agent</span>
                    <p className="font-mono text-xs text-slate-600 mt-1 break-all bg-white p-2 rounded border">
                      {selectedUserFraud.signup_metadata.user_agent || "N/A"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => onRecheckFraud(selectedUserFraud.user_id)}
                  data-testid="recheck-fraud-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Re-check Fraud
                </Button>
                {fraudCheck.should_flag && (
                  <Button
                    variant="default"
                    className="bg-green-600 hover:bg-green-700"
                    onClick={() => onClearFraudFlag(selectedUserFraud.user_id)}
                    data-testid="clear-fraud-flag-btn"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Clear Flag
                  </Button>
                )}
                {selectedUserFraud.status !== "suspended" && (
                  <Button
                    variant="destructive"
                    onClick={() => {
                      onSuspendUser(selectedUserFraud.user_id);
                      onOpenChange(false);
                    }}
                    data-testid="suspend-from-fraud-btn"
                  >
                    <Ban className="w-4 h-4 mr-2" />
                    Suspend User
                  </Button>
                )}
              </div>
            </>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <Shield className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>No fraud check data available for this user.</p>
              <p className="text-sm mt-1">
                This user may have registered before fraud detection was enabled.
              </p>
              {selectedUserFraud.signup_metadata?.ip_address && (
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={() => onRecheckFraud(selectedUserFraud.user_id)}
                  data-testid="run-fraud-check-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Run Fraud Check
                </Button>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
